// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useAnalytics from 'Hooks/useAnalytics';
import useAsyncError from 'Hooks/useAsyncError';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import {
  API_ACCOUNT_RESELLER_PROFILE,
  API_RESELLER_PROFILE,
  EMAIL_REGEX,
  USER_TYPE
} from 'Constants';
import AccountCompanyDataComponent from 'Components/Account/AccountCompanyData';

const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const CUI_CONTROL_KEY = '753217532';

const isValidRomanianCui = value => {
  const normalizedValue = String(value || '').trim().toUpperCase();
  const numericValue = normalizedValue.replace(/^RO/, '').replace(/^0+/, '');
  if (!/^\d{2,10}$/.test(numericValue)) return false;

  const reversedDigits = numericValue.split('').reverse();
  const reversedControlKey = CUI_CONTROL_KEY.split('').reverse();
  const controlDigit = Number(reversedDigits.shift());
  const sum = reversedDigits.reduce(
    (total, digit, index) => total + Number(digit) * Number(reversedControlKey[index]),
    0
  );
  const checksum = (sum * 10) % 11;

  return (checksum === 10 ? 0 : checksum) === controlDigit;
};

const defaultValues = {
  resellerName: '',
  resellerTaxIdentificationNumber: '',
  resellerRegistrationNumber: '',
  resellerHeadquartersAddress: '',
  resellerCompanyEmail: '',
  resellerPhone: '',
  logo: null,
};

const AccountCompanyData = memo(({ setErrors }) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const fetcher = useFetch();
  const throwError = useAsyncError();
  const { data: { loggedIn, type } = {} } = useUser();
  const isInternalReseller = type === USER_TYPE.INTERNAL_RESELLER;
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    setError,
    clearErrors,
    setValue,
    watch,
    handleSubmit,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldFocusError: true,
  });

  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const validators = {
    resellerName: {},
    resellerTaxIdentificationNumber: {
      maxLength: maxLength(12),
      validate: value => {
        if (!value) return true;
        if (isInternalReseller) {
          return isValidRomanianCui(value) || t('offers.invalidInternalTaxIdentificationNumber');
        }
        return /^\d+$/.test(value) || t('offers.invalidExternalTaxIdentificationNumber');
      },
    },
    resellerRegistrationNumber: {
      maxLength: maxLength(15),
      validate: value => {
        if (!value || !isInternalReseller) return true;
        return (
          /^[JFC]\d{2}\/\d{3}\/\d{4}$/.test(value)
          || /^[JFC]\d{13}$/.test(value)
          || t('offers.invalidInternalRegistrationNumber')
        );
      },
    },
    resellerHeadquartersAddress: {
      maxLength: maxLength(255),
      pattern: {
        value: /^[\p{L}\p{N}\s,.]*$/u,
        message: t('offers.invalidCompanyAddress')
      },
    },
    resellerCompanyEmail: {
      maxLength: maxLength(50),
      pattern: { value: EMAIL_REGEX, message: t('errors.invalidEmail') },
    },
    resellerPhone: {
      maxLength: maxLength(15),
      pattern: {
        value: /^\d*$/,
        message: t('offers.invalidCompanyPhone')
      },
    },
  };
  const logo = watch('logo');

  useEffect(() => {
    if (!loggedIn) return;

    setLoading(true);
    fetcher(API_RESELLER_PROFILE)
      .then(response => {
        const account = response?.account || {};
        reset({
          resellerName: account.resellerName || '',
          resellerTaxIdentificationNumber: account.resellerTaxIdentificationNumber || '',
          resellerRegistrationNumber: account.resellerRegistrationNumber || '',
          resellerHeadquartersAddress: account.resellerHeadquartersAddress || '',
          resellerCompanyEmail: account.resellerCompanyEmail || '',
          resellerPhone: account.resellerPhone || '',
          logo: account.resellerLogoUrl ? { dataUrl: account.resellerLogoUrl } : null,
        });
        setLogoFile(null);
      })
      .catch(error => throwError(error))
      .finally(() => setLoading(false));
  }, [fetcher, loggedIn, reset, throwError]);

  const handleLogoUpload = file => {
    if (!file) return;

    if (!LOGO_TYPES.includes(file.type)) {
      setError('logo', { type: 'validate', message: t('offers.companyLogoInvalidType') });
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setError('logo', { type: 'validate', message: t('offers.companyLogoTooLarge') });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      clearErrors('logo');
      setLogoFile(file);
      setValue('logo', {
        dataUrl: reader.result,
        name: file.name,
        type: file.type,
      }, { shouldDirty: true });
      analytics.track('offer_company_logo_uploaded');
    };
    reader.onerror = () => setError('logo', {
      type: 'validate',
      message: t('offers.companyLogoReadFailed'),
    });
    reader.readAsDataURL(file);
  };

  const handleLogoRemove = () => {
    clearErrors('logo');
    setLogoFile(null);
    setValue('logo', null, { shouldDirty: true });
    analytics.track('offer_company_logo_removed');
  };

  const onSubmit = values => {
    setSubmitLoading(true);
    setErrors([]);
    const payload = new FormData();
    payload.append('resellerCompanyEmail', values.resellerCompanyEmail || '');
    payload.append('resellerHeadquartersAddress', values.resellerHeadquartersAddress || '');
    payload.append('resellerPhone', values.resellerPhone || '');
    payload.append('resellerTaxIdentificationNumber', values.resellerTaxIdentificationNumber || '');
    payload.append('resellerRegistrationNumber', values.resellerRegistrationNumber || '');
    if (logoFile) {
      payload.append('logo', logoFile);
    } else if (!values.logo) {
      payload.append('removeLogo', '1');
    }

    fetcher(API_ACCOUNT_RESELLER_PROFILE, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain',
      },
      body: payload,
    }).then(() => {
      analytics.track('offer_company_data_saved');
      setErrors([{ type: 'success', message: t('offers.companyDataSaved') }]);
      reset(values);
      setLogoFile(null);
    }).catch(error => {
      const messages = (error?.info?.errors || [])
        .map(item => item.message_template)
        .filter(Boolean);
      setErrors([{
        type: 'error',
        message: messages.length
          ? messages.join('<br />')
          : t('offers.companyDataSaveFailed')
      }]);
    }).finally(() => {
      setSubmitLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  return (
    <AccountCompanyDataComponent
      validators={ validators }
      loading={ loading }
      register={ register }
      rhfErrors={ rhfErrors }
      logo={ logo }
      handleLogoUpload={ handleLogoUpload }
      handleLogoRemove={ handleLogoRemove }
      handleOnSubmit={ handleSubmit(onSubmit) }
      submitLoading={ submitLoading }
    />
  );
});

AccountCompanyData.propTypes = {
  setErrors: func,
};

AccountCompanyData.displayName = 'AccountCompanyDataContainer';

export default AccountCompanyData;
